import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const useUIStore = defineStore('ui', () => {
  // 侧边栏显示状态
  const showSidebar = ref(false)

  // 认证弹窗状态管理
  const authDialog = ref({
    show: false,
    isLogin: true, // true为登录模式，false为注册模式
    inviteCode: '' // 邀请码
  })

  // 页面状态管理
  const pageStates = ref({
    ConvidarCentro: {
      activeTab: 0
    },
    activity: {
      activeTab: 0
    }
  })

  // 活动红点状态管理
  const activityBadges = ref({
    vip: false,    // VIP红点
    rebate: false, // 返利红点
    eventos: false, // Eventos转盘红点
    chest: false,   // 宝箱红点
    mysterious: false, // 神秘抽奖红点
    dailyCashback: false, // 日返水红点
    weekCashback: false  // 周返水红点
  })

  // 推广配置管理
  const promoterConfig = ref<any>(null)
  const promoterConfigLoading = ref(false)
  const promoterConfigLoaded = ref(false)

  // 认证弹窗相关方法
  const showAuthDialog = (isLogin = true, inviteCode = '') => {
    authDialog.value.show = true
    authDialog.value.isLogin = isLogin
    authDialog.value.inviteCode = inviteCode
  }

  const hideAuthDialog = () => {
    authDialog.value.show = false
  }

  const switchAuthMode = (isLogin: boolean) => {
    authDialog.value.isLogin = isLogin
  }

  const setAuthInviteCode = (inviteCode: string) => {
    authDialog.value.inviteCode = inviteCode
  }

  // 打开侧边栏
  const openSidebar = () => {
    showSidebar.value = true
  }

  // 关闭侧边栏
  const closeSidebar = () => {
    showSidebar.value = false
  }

  // 切换侧边栏状态
  const toggleSidebar = () => {
    showSidebar.value = !showSidebar.value
  }

  // 设置页面状态
  const setPageState = (pageName: string, state: any) => {
    if (!pageStates.value[pageName]) {
      pageStates.value[pageName] = {}
    }
    pageStates.value[pageName] = { ...pageStates.value[pageName], ...state }
  }

  // 获取页面状态
  const getPageState = (pageName: string, key?: string) => {
    if (!pageStates.value[pageName]) {
      return key ? undefined : {}
    }
    return key ? pageStates.value[pageName][key] : pageStates.value[pageName]
  }

  // 设置 ConvidarCentro 页面的 activeTab
  const setConvidarCentroActiveTab = (activeTab: number) => {
    setPageState('ConvidarCentro', { activeTab })
  }

  // 获取 ConvidarCentro 页面的 activeTab
  const getConvidarCentroActiveTab = () => {
    return getPageState('ConvidarCentro', 'activeTab') || 0
  }

  // 设置 activity 页面的 activeTab
  const setActivityActiveTab = (activeTab: number) => {
    setPageState('activity', { activeTab })
  }

  // 获取 activity 页面的 activeTab
  const getActivityActiveTab = () => {
    return getPageState('activity', 'activeTab') || 0
  }

  // 设置活动红点状态
  const setActivityBadge = (type: 'vip' | 'rebate' | 'eventos' | 'chest' | 'mysterious' | 'dailyCashback' | 'weekCashback', hasBadge: boolean) => {
    activityBadges.value[type] = hasBadge
  }

  // 获取活动红点状态
  const getActivityBadge = (type: 'vip' | 'rebate' | 'eventos' | 'chest' | 'mysterious' | 'dailyCashback' | 'weekCashback') => {
    return activityBadges.value[type] || false
  }

  // 检查是否有任何活动红点（用于TabBar显示）
  const hasAnyActivityBadge = computed(() => {
    return activityBadges.value.vip || activityBadges.value.rebate || activityBadges.value.eventos || activityBadges.value.chest || activityBadges.value.mysterious || activityBadges.value.dailyCashback || activityBadges.value.weekCashback
  })

  // 获取推广配置（带缓存）
  const getPromoterConfig = async () => {
    // 如果已经加载过，直接返回
    if (promoterConfigLoaded.value && promoterConfig.value) {
      return promoterConfig.value
    }

    // 如果正在加载，等待加载完成
    if (promoterConfigLoading.value) {
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (promoterConfigLoaded.value) {
            resolve(promoterConfig.value)
          } else {
            setTimeout(checkLoaded, 100)
          }
        }
        checkLoaded()
      })
    }

    // 开始加载
    promoterConfigLoading.value = true
    try {
      const { getPromoterConfig: fetchPromoterConfig } = await import('@/api/game')
      const res = await fetchPromoterConfig()
      if (res && res.data) {
        promoterConfig.value = res.data
        promoterConfigLoaded.value = true
        console.log('Promoter config loaded:', res.data)
      }
      return promoterConfig.value
    } catch (error) {
      console.error('获取推广配置失败:', error)
      return null
    } finally {
      promoterConfigLoading.value = false
    }
  }

  // 清除推广配置缓存（用于刷新）
  const clearPromoterConfig = () => {
    promoterConfig.value = null
    promoterConfigLoaded.value = false
  }

  return {
    showSidebar,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    pageStates,
    setPageState,
    getPageState,
    setConvidarCentroActiveTab,
    getConvidarCentroActiveTab,
    setActivityActiveTab,
    getActivityActiveTab,
    activityBadges,
    setActivityBadge,
    getActivityBadge,
    hasAnyActivityBadge,
    promoterConfig,
    promoterConfigLoading,
    promoterConfigLoaded,
    getPromoterConfig,
    clearPromoterConfig,
    // 认证弹窗相关
    authDialog,
    showAuthDialog,
    hideAuthDialog,
    switchAuthMode,
    setAuthInviteCode,
  }
}, {
  persist: {
    key: 'ui-store',
    storage: sessionStorage,
    includes: ['showSidebar', 'pageStates', 'activityBadges'],
  },
})

export default useUIStore
